import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Selector, { OptionKV } from "~styled/selector/Selector";
import pxToRem from "~libs/pxToRem";
import { makeStyles } from "@mui/styles";
import ItemLoadingCard from "./ItemLoadingCard";
import { Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { TIPO_SINIESTRO_PLACEHOLDER } from "~constants/tipoSiniestro";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "2%",
    borderBottom: "1px solid #8B8B8B",
  },
  caption: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: 600,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "left",
    color: "#8B8B8B",
  },
  body: {
    display: "flex",
    justifyContent: "flex-end",
    maxWidth: pxToRem(270),
    width: "50%",
  },
  selector: {
    width: "100%",
  },
  value: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: "bold",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "right",
    color: "#424242",
  },
}));

const errorText = "Requerido";

interface ItemSelectorCardProps<T> {
  id: string;
  label: string;
  value: T;
  onChange: (data: { value: T; valid: boolean; focus: boolean }) => void;
  options: OptionKV<T>[];
  valueOptions?: Record<string, string>;
  variant?: "normal" | "big";
  isLoading?: boolean;
  displayError?: boolean;
  readOnly?: boolean;
  required: boolean;
}

function ItemSelectorCard<T>({
  id,
  label,
  value,
  onChange,
  options,
  valueOptions,
  variant = "normal",
  isLoading = false,
  displayError = false,
  readOnly = false,
  required = true,
}: ItemSelectorCardProps<T>) {
  const classes = useStyles();
  const [localValue, setValue] = useState<T>(value);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setValue(value);
  }, [value]);

  const validate = (val) => (required ? !!val : true);

  const handleChangeValue = (newValue: T) => {
    setValue(newValue);
    onChange?.({ value: newValue, valid: true, focus: false });
  };

  if (isLoading || !options) return <ItemLoadingCard label={label} />;

  const optionsInverse = getOptionsInverse(valueOptions, options);
  const showError = !validate(localValue) && (touched || displayError);

  return (
    <div className={classes.container}>
      <Typography className={classes.caption}>{label}</Typography>
      <div className={classes.body}>
        {!readOnly && (
          <div className={classes.selector}>
            <Selector<T>
              id={id}
              value={
                optionsInverse[`${localValue}`] || TIPO_SINIESTRO_PLACEHOLDER
              }
              options={options}
              onChange={handleChangeValue}
              error={showError}
              variant={variant}
            />
          </div>
        )}
        {readOnly && value !== undefined && (
          <Typography className={classes.value}>
            {optionsInverse[`${value}`]}
          </Typography>
        )}
        {readOnly && value === undefined && (
          <div className={classes.value}>&mdash;</div>
        )}
        {displayError && (
          <Tooltip title={errorText}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                color: "#f77221",
                paddingLeft: 4,
                cursor: "pointer",
              }}
            >
              <InfoOutlinedIcon style={{ width: 30, height: 30 }} />
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default ItemSelectorCard;

function getOptionsInverse<T>(
  valueOptions?: Record<string, string>,
  options?: OptionKV<T>[]
): Record<string, string> {
  const optionsInverse: Record<string, string> = {};
  if (options) {
    options.forEach((option) => {
      optionsInverse[String(option.value)] = option.label;
    });
  }

  return valueOptions ?? optionsInverse;
}
